import { zodResolver } from '@hookform/resolvers/zod';
import { Tooltip } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Select } from 'src/components/molecules/select';
import { IssueStatus } from 'src/features/issues/api/types/response/issue.response';
import { issueStateColors } from 'src/features/issues/constants/issue-state/issue-state-colors';
import { z } from 'zod';
import { useIssueStatusFormValidationSchema } from '../../templates/issue-details/schema/use-form-validation-schema';
import { useEffect, useMemo } from 'react';
import { Status } from 'src/components/atoms/status';
import { issueStateTranslations } from 'src/features/issues/constants/issue-state/issue-state-translations';
import { useEditIssueStatus } from 'src/features/issues/api/hooks/use-edit-issue-status';
import { isStatusAvailable } from 'src/features/issues/utils/is-status-available';

interface Props extends DialogActions {
  token: string;
  currentStatus: IssueStatus;
  currentComment: string;
}

export default function EditIssueStatusDialog({
  open,
  onCancel,
  token,
  currentStatus,
  currentComment,
}: Props) {
  const { t } = useTranslation(['issues', 'validation']);

  const validationSchema = useIssueStatusFormValidationSchema();

  type FormValues = z.infer<typeof validationSchema>;

  const defaultValues: FormValues = useMemo(
    () => ({
      status: currentStatus,
      comment: currentComment || '',
    }),
    [currentStatus, currentComment],
  );

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: editIssueStatus, isPending: isEditingIssueStatus } =
    useEditIssueStatus(handleOnCancel);

  const handleEditIssueStatus = (data: FormValues) => {
    editIssueStatus({ token, newStatus: data.status, comment: data.comment });
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const selectedStatus = useWatch({
    control,
    name: 'status',
  });

  return (
    <Dialog
      open={open}
      title={t('issues:common.editIssueStatusDialog.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleEditIssueStatus)}
      isConfirmButtonSubmitting={isEditingIssueStatus}
      isConfirmButtonDisabled={selectedStatus === currentStatus}
      scrollable>
      <Controller
        control={control}
        name="status"
        render={({
          field: { name, value, onChange, onBlur },
          fieldState: { error, invalid },
        }) => (
          <Select
            name={name}
            value={value}
            sx={{ mb: 3 }}
            items={[
              {
                leadingSlot: (
                  <Status
                    spacing={1}
                    color={issueStateColors.NEW}
                    label={t(issueStateTranslations.NEW)}
                  />
                ),
                label: '',
                value: 'NEW',
                disabled: !isStatusAvailable('NEW', currentStatus),
              },
              {
                leadingSlot: (
                  <Status
                    spacing={1}
                    color={issueStateColors.IN_PROGRESS}
                    label={t(issueStateTranslations.IN_PROGRESS)}
                  />
                ),
                label: '',
                value: 'IN_PROGRESS',
                disabled: !isStatusAvailable('IN_PROGRESS', currentStatus),
              },
              {
                leadingSlot: (
                  <Status
                    spacing={1}
                    color={issueStateColors.RESOLVED}
                    label={t(issueStateTranslations.RESOLVED)}
                  />
                ),
                label: '',
                value: 'RESOLVED',
                disabled: !isStatusAvailable('RESOLVED', currentStatus),
              },
              {
                leadingSlot: (
                  <Status
                    spacing={1}
                    color={issueStateColors.REJECTED}
                    label={t(issueStateTranslations.REJECTED)}
                  />
                ),
                label: '',
                value: 'REJECTED',
                disabled: !isStatusAvailable('REJECTED', currentStatus),
              },
            ]}
            error={invalid}
            helperText={error?.message}
            onChange={onChange}
            onBlur={onBlur}
            fullWidth
            required
          />
        )}
      />
      <Controller
        control={control}
        name="comment"
        render={({
          field: { name, value, onChange, onBlur },
          fieldState: { error, invalid },
        }) => (
          <Tooltip
            title={t(
              'issues:common.editIssueStatusDialog.commentTooltipMessage',
            )}
            placement="bottom-end"
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [12, -65],
                  },
                },
              ],
            }}
            disableFocusListener={
              selectedStatus !== 'NEW' && selectedStatus !== 'IN_PROGRESS'
            }
            disableHoverListener={
              selectedStatus !== 'NEW' && selectedStatus !== 'IN_PROGRESS'
            }>
            <InputField
              name={name}
              label={t('issues:common.editIssueStatusDialog.commentLabel')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={invalid}
              helperText={error?.message}
              fullWidth
              multiline
              rows={5}
              disabled={
                selectedStatus === 'NEW' || selectedStatus === 'IN_PROGRESS'
              }
            />
          </Tooltip>
        )}
      />
    </Dialog>
  );
}
